import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Avatar,
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
  Button,
  TextField,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import moment from 'moment'
import API from '../../../../../services/API'
import { ControlRoomContext } from '../ControlPage'
import { GlobalContext, UserContext } from '../../../../../App'
import { Send } from '@mui/icons-material'
import { useIncidentLogs } from './useIncidentLogs'

const IncidentLogs = () => {
  const {
    selectedIncident,
    selectedControlRoom,
    canDispatch = true,
  } = useContext(ControlRoomContext)
  const { setToast } = useContext(GlobalContext)
  const { user } = useContext(UserContext)
  const incidentID = selectedIncident?.id || selectedIncident?._id
  const { logs, loading, hasMore, loadMore } = useIncidentLogs(incidentID)

  const [selectedLog, setSelectedLog] = useState(null)
  const [newLogText, setNewLogText] = useState('')
  const [pendingLogs, setPendingLogs] = useState([])

  const allLogs = useMemo(() => {
    return [...pendingLogs, ...(logs ?? [])]
  }, [pendingLogs, logs])

  useEffect(() => {
    if (!pendingLogs.length || !logs?.length) return

    setPendingLogs((prevPending) =>
      prevPending.filter((pendingLog) => {
        const matchedLog = logs.some((serverLog) => {
          const sameAction = serverLog.action === pendingLog.action
          const sameAuthor = serverLog.author?._id
            ? serverLog.author._id === user?._id
            : `${serverLog.author?.firstName || ''} ${serverLog.author?.lastName || ''}`.trim() ===
              `${pendingLog.author?.firstName || ''} ${pendingLog.author?.lastName || ''}`.trim()

          const closeInTime =
            Math.abs(
              moment(serverLog.timestamp).diff(
                moment(pendingLog.timestamp),
                'seconds',
              ),
            ) <= 10

          return sameAction && sameAuthor && closeInTime
        })

        return !matchedLog
      }),
    )
  }, [logs, pendingLogs.length, user?._id])

  const handleSubmitNewLog = async () => {
    const trimmedText = newLogText.trim()
    if (!trimmedText || !selectedIncident?.id || !selectedControlRoom) return

    const tempId = `temp-${Date.now()}`

    const optimisticLog = {
      _id: tempId,
      tempId,
      action: trimmedText,
      timestamp: new Date().toISOString(),
      optimistic: true,
      author: {
        firstName: user?.firstName || 'You',
        lastName: user?.lastName || '',
        photograph: user?.photograph || null,
      },
    }

    setPendingLogs((prev) => [optimisticLog, ...prev])
    setNewLogText('')

    try {
      const res = await API.post(
        `/commandcontrol/incidents/${selectedIncident.id}/newlog`,
        {
          action: trimmedText,
          controlRoomID: selectedControlRoom,
        },
      )

      if (res.status !== 200) {
        setPendingLogs((prev) => prev.filter((log) => log._id !== tempId))
        setNewLogText(trimmedText)
        setToast({ message: 'Unable to add log', severity: 'error' })
      }
    } catch (e) {
      setPendingLogs((prev) => prev.filter((log) => log._id !== tempId))
      setNewLogText(trimmedText)
      setToast({ message: 'Unable to add log', severity: 'error' })
    }
  }

  return (
    <Grid item xs={12}>
      <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Incident Logs
        </Typography>

        {loading && allLogs.length === 0 && <LinearProgress />}

        {canDispatch && (
          <Stack direction={{ xs: 'column', lg: 'row' }} gap={1}>
            <TextField
              fullWidth
              value={newLogText}
              onChange={(e) => setNewLogText(e.target.value)}
              placeholder="New Log..."
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSubmitNewLog()
                }
              }}
            />
            <Button
              variant="contained"
              color="success"
              disabled={!newLogText.trim()}
              onClick={handleSubmitNewLog}
            >
              <Send />
            </Button>
          </Stack>
        )}

        {allLogs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No logs yet.
          </Typography>
        ) : (
          <Stack gap={1}>
            <List disablePadding>
              {allLogs.map((log, i) => (
                <React.Fragment key={log._id || i}>
                  <ListItem alignItems="flex-start" sx={{ py: 1.5 }}>
                    <ListItemAvatar>
                      <Avatar src={log.author?.photograph}>
                        {`${log.author?.firstName?.[0] || ''}${log.author?.lastName?.[0] || ''}` ||
                          '?'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography fontWeight={600}>
                            {`${log.author?.firstName || ''} ${log.author?.lastName || ''}`.trim() ||
                              log.authorName ||
                              'Unknown'}
                          </Typography>
                          {log.source === 'chat' && (
                            <Chip
                              size="small"
                              color={log.fromControl ? 'primary' : 'secondary'}
                              variant="outlined"
                              label={
                                log.fromControl
                                  ? `Control → ${log.callSign || 'Crew'}`
                                  : `${log.callSign || 'Crew'} → Control`
                              }
                            />
                          )}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontStyle: 'italic' }}
                          >
                            {moment(log.timestamp).format('DD MMM YYYY, HH:mm')}
                            {log.optimistic ? ' • Sending...' : ''}
                          </Typography>
                        </Stack>
                      }
                      secondary={
                        <Typography variant="body2" color="text.primary">
                          {log.action}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {i < allLogs.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>

            {hasMore && !loading && (
              <Box textAlign="center" mt={2}>
                <Button variant="outlined" onClick={loadMore}>
                  Load More
                </Button>
              </Box>
            )}

            {loading && allLogs.length > 0 && <LinearProgress />}
          </Stack>
        )}
      </Paper>
    </Grid>
  )
}

export default IncidentLogs
